import { useState } from "react";
import HighRiskIcon from "../../../assets/high-risk-dashboard.svg";
import LimitedRiskIcon from "../../../assets/limited-risk-dashboard.svg";
import MinimalRiskIcon from "../../../assets/minimal-risk-dashboard.svg";
import ProhibitedIcon from "../../../assets/prohibited-dashboard.svg";
import { Divider } from "../../ui/Divider/Divider";

interface RiskCategoryItemProps {
  title: string;
  description: string;
  examples: string[];
  icon: React.ReactNode;
  defaultExpanded?: boolean;
  isFirst?: boolean;
  isLast?: boolean;
}

function RiskCategoryItem({
  title,
  description,
  examples,
  icon,
  defaultExpanded = false,
  isFirst = false,
  isLast = false,
}: RiskCategoryItemProps) {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);

  return (
    <>
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between transition-colors"
      >
        <div className="flex items-center gap-2">
          <div className="flex items-center justify-center w-8 h-8">{icon}</div>
          <span className="text-gray-950 font-medium text-base leading-6">
            {title}
          </span>
        </div>
        <svg
          className={`w-5 h-5 text-gray-400 transition-transform ${
            isExpanded ? "rotate-180" : ""
          }`}
          fill="none"
          strokeWidth="2"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </button>

      {isExpanded && (
        <div className="pl-10">
          <p className="text-gray-800 text-sm text-left leading-5 font-normal">
            {description}
          </p>
          <div>
            <p className="text-gray-950 font-medium text-sm leading-5 text-left pt-1">
              Examples:
            </p>
            <ul className="pl-2">
              {examples.map((example, index) => (
                <li
                  key={index}
                  className="text-gray-800 text-sm leading-5 font-normal flex items-start"
                >
                  <span className="inline-block w-1 h-1 rounded-full bg-zinc-900 mt-[7px] mr-2.5 flex-shrink-0 text-left"></span>
                  {example}
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {!isLast && (
        <div className="h-[28px] flex items-center w-full">
          <Divider classes="w-full" />
        </div>
      )}
    </>
  );
}

export function RiskCategories() {
  return (
    <div className="bg-white rounded-lg border border-gray-250v2 shadow-sm px-6 py-6">
      <div className="flex flex-col gap-4">
        <h3 className="text-gray-550 font-medium text-xs leading-4 uppercase text-left">
          UNDERSTANDING AI SYSTEM RISK CATEGORIES
        </h3>
        <p className="text-gray-800 text-sm leading-5 font-normal text-left">
          The AI Act classifies AI systems into four risk categories, each with
          different compliance requirements. Understanding these categories is
          crucial for determining your obligations.
        </p>
        <div>
          <RiskCategoryItem
            title="Prohibited"
            description="AI systems that pose unacceptable risks and are banned in the EU"
            examples={[
              "Social scoring systems by governments",
              "Real-time biometric identification in public spaces",
              "AI systems that manipulate human behavior",
              "Subliminal techniques beyond consciousness",
            ]}
            icon={<img src={ProhibitedIcon} alt="Prohibited" className="w-8 h-8" />}
            defaultExpanded={false}
            isFirst={true}
          />

          <RiskCategoryItem
            title="High-Risk"
            description="AI systems with significant impact on safety or fundamental rights"
            examples={[
              "Medical devices and diagnostics",
              "Critical infrastructure management",
              "Educational assessment systems",
              "HR recruitment and evaluation tools",
            ]}
            icon={<img src={HighRiskIcon} alt="High Risk" className="w-8 h-8" />}
            defaultExpanded={true}
          />

          <RiskCategoryItem
            title="Limited-Risk"
            description="AI systems requiring transparency obligations"
            examples={[
              "Chatbots and virtual assistants",
              "AI-generated content systems",
              "Emotion recognition systems",
              "Deepfake detection tools",
            ]}
            icon={<img src={LimitedRiskIcon} alt="Limited Risk" className="w-8 h-8" />}
            defaultExpanded={false}
          />

          <RiskCategoryItem
            title="Minimal-Risk"
            description="AI systems with minimal regulatory requirements"
            examples={[
              "Spam filters and basic recommendations",
              "Simple inventory management",
              "Basic data analytics tools",
              "Non-critical automation systems",
            ]}
            icon={<img src={MinimalRiskIcon} alt="Minimal Risk" className="w-8 h-8" />}
            defaultExpanded={false}
            isLast={true}
          />
        </div>
      </div>
    </div>
  );
}
